import { useEffect, useMemo, useState } from 'react'
import { subscribeChecklistItemsView } from '../../services/checklistReader'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useFocusMode } from '../../context/useFocusMode'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useAppDataRefreshSignal } from '../../hooks/useAppDataRefreshSignal'
import { TaskCarousel } from '../Helper/TaskCarousel'
import { ChecklistDetailsModal } from '../Helper/ChecklistDetailsModal'
import { Button, Pressable } from '../Common'
import { submitHelperProof } from '../../services/helper/helperChecklistDetailsService'
import { type HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import { isChecklistAssignedToUser, isChecklistCurrentlyActionable } from '../../services/checklistVisibility'
import { currentOccurrenceKeyForItem } from '../../services/recurringChecklistService'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import type { HelperWorkspaceSummary } from '../../services/helper/helperStore'
import type { ChecklistItem } from '../../types/schema'
import type { Workspace } from '../../types/workspace'
import { getChecklistDependencyBlockState } from '../../utils/checklistBlockedTodoPolicy'
import { isChecklistGate } from '../../utils/checklistDependencyState'
import {
  compareMemberForYouDueAscending,
  projectMemberForYouAssignedItem,
} from './memberForYouModel'
import { toMemberTodosWorkspaceSummary } from './MemberTodos.helpers'
import { ClockCard } from '../Timesheets/ClockCard'

/**
 * Member "Todos" tab — the helper Tasks surface (swipeable card carousel +
 * slide-up details modal) driven by the member's own assigned checklist items
 * in the active workspace.
 */
export const MemberTodos = ({
  workspace,
  onOpenChecklistItem,
  onOpenTodos,
}: {
  workspace: Workspace
  onOpenChecklistItem?: (item: HelperAssignedItem) => void
  onOpenTodos?: () => void
}) => {
  const { timeZone, todayIso, weekKey } = useWorkspaceDay(workspace.id)
  const { currentUser, userProfile } = useAuth()
  const { showToast } = useToast()
  const focusMode = useFocusMode()
  const { registerTaskFeed } = focusMode
  const displayName = useDisplayName(userProfile ?? currentUser)
  const uid = currentUser?.uid ?? null
  const refreshToken = useAppDataRefreshSignal()
  const feedKey = `${uid ?? 'anon'}:${workspace.id}`
  const [feed, setFeed] = useState<{
    key: string
    activeItems: HelperAssignedItem[]
    upcomingItems: HelperAssignedItem[]
    archivedItems: HelperAssignedItem[]
    loading: boolean
  }>(() => ({
    key: feedKey,
    activeItems: [],
    upcomingItems: [],
    archivedItems: [],
    loading: true,
  }))
  const [selectedItem, setSelectedItem] = useState<HelperAssignedItem | null>(null)
  const [taskFilter, setTaskFilter] = useState<'ACTIVE' | 'UPCOMING' | 'PAST' | 'ALL'>('ACTIVE')
  // Per-item chosen occurrence pageKey. Falls back to today's key via
  // `currentOccurrenceKeyForItem` when the user hasn't picked anything —
  // matches the pre-existing PROOF quick-action default. Retargeted uploads
  // let a helper attach proof to a past/future recurring instance from the
  // same card without leaving Todos.
  const [occurrenceKeyByItemId, setOccurrenceKeyByItemId] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!uid) return
    const key = `${uid}:${workspace.id}`
    // Same query shape as the For You page (workspace scoped, assignee
    // filtered client-side) so no new composite index is required.
    return subscribeChecklistItemsView(
      { workspaceId: workspace.id },
      (rawItems) => {
        const allItems = rawItems.map((raw) => projectMemberForYouAssignedItem(raw))
        const itemById = new Map(allItems.map((item) => [item.id, item as ChecklistItem]))
        const assignedItems = allItems
          .filter((item) => !isChecklistGate(item))
          .filter((item) => (item.board ?? 'DASHBOARD') === 'DASHBOARD')
          .filter((item) => isChecklistAssignedToUser(item, uid))
          .map((item) => {
            const targetPeriodKey = occurrenceKeyByItemId[item.id] ?? (item.checklistType === 'WEEKLY' ? weekKey : todayIso)
            const dependencyState = getChecklistDependencyBlockState(item, itemById, targetPeriodKey)
            return {
              ...item,
              dependencyBlocked: dependencyState.isBlocked,
              dependencyBlockReasons: dependencyState.reasons.map((reason) => reason.title ?? reason.targetId ?? 'Dependency'),
            }
          })

        const activeItems = assignedItems
          .filter((item) => isChecklistCurrentlyActionable(item, uid))
          .sort(compareMemberForYouDueAscending)
        const secondary = assignedItems.filter((item) => !isChecklistCurrentlyActionable(item, uid))
        const upcomingItems = secondary
          .filter((item) => item.timeStatus === 'upcoming')
          .sort(compareMemberForYouDueAscending)
        const archivedItems = secondary
          .filter((item) => item.timeStatus !== 'upcoming')
          .sort(compareMemberForYouDueAscending)

        setFeed({
          key,
          activeItems,
          upcomingItems,
          archivedItems,
          loading: false,
        })
      },
      (err) => {
        console.warn('[member-todos] checklist subscription failed', err)
        showToast("Couldn't load checklists — you may not have access. Contact your workspace admin.")
      },
    )
  }, [refreshToken, showToast, uid, workspace.id])

  const activeItems = useMemo(
    () => (feed.key === feedKey ? feed.activeItems : []),
    [feed.activeItems, feed.key, feedKey],
  )
  const upcomingItems = useMemo(
    () => (feed.key === feedKey ? feed.upcomingItems : []),
    [feed.key, feed.upcomingItems, feedKey],
  )
  const archivedItems = useMemo(
    () => (feed.key === feedKey ? feed.archivedItems : []),
    [feed.archivedItems, feed.key, feedKey],
  )
  const allKnownItems = useMemo(
    () => [...activeItems, ...upcomingItems, ...archivedItems],
    [activeItems, upcomingItems, archivedItems],
  )
  const taskCounts = useMemo(() => ({
    active: activeItems.length,
    upcoming: upcomingItems.length,
    past: archivedItems.length,
    all: allKnownItems.length,
  }), [activeItems.length, allKnownItems.length, archivedItems.length, upcomingItems.length])
  const filteredItems = useMemo(() => {
    if (taskFilter === 'ACTIVE') return activeItems
    if (taskFilter === 'UPCOMING') return upcomingItems
    if (taskFilter === 'PAST') return archivedItems
    return allKnownItems
  }, [activeItems, allKnownItems, archivedItems, taskFilter, upcomingItems])
  useEffect(() => {
    registerTaskFeed({ items: allKnownItems, openTodos: onOpenTodos ?? (() => undefined) })
  }, [allKnownItems, onOpenTodos, registerTaskFeed])
  const loading = uid ? feed.key !== feedKey || feed.loading : false
  // Resolve the open modal against live data so status changes reflect
  // immediately; keep the last snapshot if the item leaves the active feed.
  const detailsItem = selectedItem
    ? allKnownItems.find((item) => item.id === selectedItem.id) ?? selectedItem
    : null
  const setDetailsItem = setSelectedItem

  const actor = useMemo(
    () => (currentUser ? { id: currentUser.uid, name: displayName || currentUser.email || 'Member' } : null),
    [currentUser, displayName],
  )

  const workspaceSummaries = useMemo<HelperWorkspaceSummary[]>(
    () => [toMemberTodosWorkspaceSummary(workspace)],
    [workspace],
  )

  return (
    <>
      <ClockCard workspace={workspace} actorRole="MEMBER" className="mb-3" />
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar snap-x snap-mandatory py-1 mb-3">
        {(['ACTIVE', 'UPCOMING', 'PAST', 'ALL'] as const).map((key) => {
          const isSelected = taskFilter === key
          const count = key === 'ACTIVE' ? taskCounts.active
            : key === 'UPCOMING' ? taskCounts.upcoming
            : key === 'PAST' ? taskCounts.past
            : taskCounts.all
          const labels: Record<typeof key, string> = {
            ACTIVE: 'Active',
            UPCOMING: 'Upcoming',
            PAST: 'Past',
            ALL: 'All',
          }
          return (
            <Pressable
              key={key}
              aria-pressed={isSelected}
              onClick={() => setTaskFilter(key)}
              className={`snap-start shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                isSelected
                  ? 'bg-[var(--accent-soft)] text-[var(--accent-strong)] border border-[color-mix(in_srgb,var(--accent)_40%,transparent)] shadow-sm'
                  : 'bg-[var(--surface-muted)] text-[var(--muted)] border border-[var(--border-subtle)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-strong)]'
              }`}
            >
              <span>{labels[key]}</span>
              <span className={`px-1.5 py-0.2 rounded-full text-[10px] bg-[var(--surface-raised)] ${count === 0 ? 'text-[var(--muted)]' : 'text-[var(--text-strong)]'}`}>
                {count}
              </span>
            </Pressable>
          )
        })}
      </div>
      <TaskCarousel
        items={filteredItems}
        loading={loading}
        workspaces={workspaceSummaries}
        blockedTodoPolicy={workspace.checklistBlockedTodoPolicy}
        emptyStateTitle={
          taskFilter === 'ACTIVE'
            ? 'No active tasks today'
            : taskFilter === 'UPCOMING'
              ? 'No upcoming tasks'
              : taskFilter === 'PAST'
                ? 'No past tasks'
                : undefined
        }
        emptyStateBody={
          taskFilter === 'ACTIVE' && (upcomingItems.length > 0 || archivedItems.length > 0)
            ? `You have ${upcomingItems.length} upcoming and ${archivedItems.length} past tasks.`
            : filteredItems.length === 0 && allKnownItems.length > 0
              ? 'No tasks found for this filter.'
              : undefined
        }
        emptyStateAction={
          filteredItems.length === 0 && allKnownItems.length > 0 && taskFilter !== 'ALL'
            ? (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setTaskFilter('ALL')}
                  className="mt-2"
                >
                  Show all tasks
                </Button>
              )
            : undefined
        }
        onOpenDetails={(item) => {
          if (onOpenChecklistItem) {
            onOpenChecklistItem(item)
            return
          }
          setDetailsItem(item)
        }}
        getTargetOccurrenceKey={(item) => {
          if (item.checklistType !== 'DAILY' && item.checklistType !== 'WEEKLY') return null
          return occurrenceKeyByItemId[item.id] ?? currentOccurrenceKeyForItem(item, new Date(), timeZone)
        }}
        onSelectOccurrence={(item, pageKey) => {
          setOccurrenceKeyByItemId((current) => ({ ...current, [item.id]: pageKey }))
        }}
        onQuickAction={(item, kind, payload) => {
          if (!actor) return
          if (kind === 'PROOF' && payload.file) {
            // Recurring items default to today's key (matches the historical
            // behaviour) but honour a helper's explicit selection from the
            // in-card RecurringOccurrenceChip when present. Non-recurring
            // items always use null pageKey.
            const recurringMode = item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY'
              ? item.checklistType
              : null
            const pageKey = recurringMode
              ? (occurrenceKeyByItemId[item.id] ?? currentOccurrenceKeyForItem(item, new Date(), timeZone))
              : null
            void submitHelperProof({
              item: {
                id: item.id,
                workspaceId: item.workspaceId,
                title: item.title,
                updatedAtIso: item.updatedAtIso ?? null,
              },
              actor,
              file: payload.file,
              pageKey,
              actorRole: 'MEMBER',
            }).catch((error) => console.warn('[member-todos] proof failed', error))
          }
          if (kind === 'LOCATION') {
            setDetailsItem(item)
          }
        }}
      />
      {(upcomingItems.length > 0 || archivedItems.length > 0) && (
        <div className="mt-4 flex flex-col gap-3 px-1 pb-6" data-testid="member-todos-secondary-sections">
          {upcomingItems.length > 0 && (
            <details className="group rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3" open={activeItems.length === 0}>
              <summary className="flex cursor-pointer items-center justify-between font-medium text-[var(--text-strong)] select-none text-sm">
                <span>Upcoming ({upcomingItems.length})</span>
                <span className="text-xs text-[var(--muted)] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-3 flex flex-col gap-2">
                {upcomingItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => onOpenChecklistItem ? onOpenChecklistItem(item) : setDetailsItem(item)}
                    className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--surface-card)] p-3 cursor-pointer hover:border-[var(--border-strong)] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-[var(--text-strong)] truncate">{item.title}</div>
                      <div className="text-xs text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))] mt-0.5">Upcoming</div>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
          {archivedItems.length > 0 && (
            <details className="group rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3">
              <summary className="flex cursor-pointer items-center justify-between font-medium text-[var(--text-strong)] select-none text-sm">
                <span>Archived ({archivedItems.length})</span>
                <span className="text-xs text-[var(--muted)] group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-3 flex flex-col gap-2">
                {archivedItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => onOpenChecklistItem ? onOpenChecklistItem(item) : setDetailsItem(item)}
                    className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--surface-card)] p-3 cursor-pointer hover:border-[var(--border-strong)] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-[var(--muted)] truncate">{item.title}</div>
                      <div className="text-xs text-[var(--muted)] mt-0.5">
                        {item.timeStatus === 'archived' ? 'Past schedule' : (item.effectiveStatus ?? 'Inactive')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
      {detailsItem ? (
        <ChecklistDetailsModal
          item={detailsItem}
          actor={actor}
          blockedTodoPolicy={workspace.checklistBlockedTodoPolicy}
          onClose={() => setDetailsItem(null)}
          actorRole="MEMBER"
        />
      ) : null}
    </>
  )
}
